/**
 * The Insights panel's **shape builders** — one slice of `/analysis/insights` in, the props
 * a chart already understands out.
 *
 * Client, 15 Sep 2026: *"the hotspot rankings should be by watershed first, then let's have
 * another one by partnerships, maybe using a pie chart"*, *"for the landcover we can use a
 * stacked bar plot to show all classes per country"*, *"a multiline graph to show changes in
 * the various landcover overall for all four countries first and also respond to filters."*
 *
 * They live here rather than inside the panel for two reasons. The panel is then a list of
 * blocks in an order — which is the thing the client actually asked us to make legible — and
 * the arithmetic that decides *which* six classes get a line is testable without a DOM. All
 * three are pure: no `t()`, no store, no formatting. A caller passes the one translated word
 * each of them needs ("Other") in, so the builder never has to know which language the page
 * is in (§F12).
 *
 * **Nothing is recomputed here.** Every percentage is the server's own (`insights.py`); a
 * second implementation of "what share of Mali is cropland" is a second set of numbers that
 * can disagree with the map next to it.
 */
import type { BarsHSeries } from '../../components/charts/BarsH';
import type { DonutItem } from '../../components/charts/Donut';
import type { LineSeries } from '../../components/charts/LineChart';
import type {
  InsightsClass,
  InsightsCountry,
  InsightsPartnerships,
  InsightsTrendPoint,
} from '../../lib/analysis';

/** How many classes get a line of their own before the rest are summed. */
export const CLASS_TREND_TOP = 6;

/**
 * The ink for a remainder — the "other" trend line and the pie's leftover wedge.
 *
 * Deliberately a neutral grey rather than the next colour in a categorical palette: "other"
 * is not a category, and giving it one would let a reader compare it with the named ones.
 */
export const OTHER_COLOR = '#B9C0B5';

/** The fallback for a class the catalogue gave no colour. Matches `ClassBars`'s own. */
const UNKNOWN_CLASS_COLOR = '#8B958A';

/**
 * The partnership pie's palette — eight hues, because `PARTNERSHIP_SLICES = 8` server-side.
 *
 * Partnerships have no colour in the catalogue (they are org units, not map classes), so the
 * wedges need a palette of their own. It is the portal's own §G8 categorical set, ordered so
 * that neighbouring wedges — which is where a pie is hardest to read — never share a hue
 * family.
 */
export const PARTNERSHIP_COLORS = [
  '#2E7D32',
  '#2B6CB0',
  '#C77B21',
  '#8E3B62',
  '#6B8E23',
  '#456082',
  '#B07818',
  '#4A8C8C',
] as const;

/**
 * A donut wedge, as this module builds them.
 *
 * `id` is the org unit behind the wedge — the stable React key and the `data-inspartner`
 * hook. Two partnerships under different watersheds can share a name (`Goulbi`), so keying
 * the legend by the label is keying it by something that is not unique. The folded remainder
 * has no unit of its own and takes the `other` sentinel.
 */
export interface DonutSlice extends DonutItem {
  id: string;
}

/** The `id` of the wedge the server folded the tail into. */
export const OTHER_SLICE_ID = '__other__';

/** `value` → the legend's colour and label, where the legend carries the class at all. */
function legendIndex(legend: readonly InsightsClass[] | null | undefined) {
  return new Map((legend ?? []).map((c) => [c.value, c]));
}

const pctOf = (classes: readonly InsightsClass[] | undefined, value: number) =>
  classes?.find((c) => c.value === value)?.pct ?? null;

/**
 * Does this epoch have a composition at all?
 *
 * The distinction the trend turns on. `insights.py` builds an epoch's `classes` from
 * `counts.items()`, so a class with **no pixels that year is absent from the list**, not
 * present at zero. Reading that absence as "not measured" breaks the line, and a broken line
 * in this chart says the harvest failed — when the truth is that the class went to zero,
 * which is the single most interesting thing a land-cover trend can show. So an epoch that
 * *has* a composition answers 0 for every class that is not in it, and only an epoch with no
 * composition at all (a failed or unharvested year) is allowed a gap.
 */
const measured = (point: InsightsTrendPoint) => (point.classes ?? []).length > 0;

/**
 * **Change over time, one line per land-cover class** (client item 7).
 *
 * `points` are the epochs the server already plotted the modal share over; each categorical
 * one carries its whole composition, so the multi-line chart needs no second request.
 *
 * * **Ranked by the latest epoch**, not by the first and not by the average: the reader's
 *   question is "what is this made of now, and how did it get there", so the legend order
 *   and the direct end labels agree with the right-hand edge of the chart.
 * * **The tail is summed into one "other" line** rather than dropped — a chart of six lines
 *   whose values do not add up to the whole is a chart that quietly lost a third of the
 *   landscape. It is only added when there is in fact a tail.
 * * **An epoch with no composition leaves a gap** (`null`), which `LineChart` breaks the
 *   line across rather than drawing a fabricated segment through.
 */
export function classTrendSeries(
  points: readonly InsightsTrendPoint[],
  legend: readonly InsightsClass[] | null | undefined,
  top: number = CLASS_TREND_TOP,
  otherLabel = 'Other',
): { labels: string[]; series: LineSeries[] } {
  const labels = points.map((p) => String(p.year ?? p.period ?? ''));
  // The ranking epoch is the newest one that has a composition at all — a series whose last
  // harvest failed still ranks by the newest year it *can* rank by.
  const ranked = [...points].reverse().find((p) => (p.classes ?? []).length > 0)?.classes;
  if (!ranked?.length) return { labels: [], series: [] };

  const order = [...ranked].sort((a, b) => (b.pct ?? 0) - (a.pct ?? 0));
  const kept = order.slice(0, Math.max(1, top));
  const keptValues = new Set(kept.map((c) => c.value));
  const index = legendIndex(legend);

  const series: LineSeries[] = kept.map((c) => {
    const entry = index.get(c.value);
    return {
      name: entry?.label ?? c.label,
      color: entry?.color ?? c.color ?? UNKNOWN_CLASS_COLOR,
      // Absent from a composition that exists is a real 0; absent because the epoch has no
      // composition is a gap. See `measured`.
      values: points.map((p) => (measured(p) ? (pctOf(p.classes, c.value) ?? 0) : null)),
    };
  });

  // The remainder, per epoch. Summed from each epoch's *own* tail rather than from the
  // ranking epoch's, so a class that was large in 2000 and is gone today still shows up in
  // 2000's "other" instead of vanishing from the chart's total.
  const other = points.map((p) => {
    if (!measured(p)) return null;
    // An epoch whose whole composition is in the top N has a tail of exactly nothing — 0,
    // not a gap, for the same reason a class absent from a composition is 0.
    return (p.classes ?? [])
      .filter((c) => !keptValues.has(c.value))
      .reduce((sum, c) => sum + (c.pct ?? 0), 0);
  });
  if (other.some((v) => v !== null && v > 0)) {
    series.push({ name: otherLabel, color: OTHER_COLOR, values: other });
  }

  return { labels, series };
}

/**
 * **All classes per country, as one stacked bar each** (client item 6).
 *
 * The block this replaces printed each country's *modal* class and its share, which answers
 * "what is most of Mali" and nothing else — four bars a reader cannot add up. A stack is the
 * same four countries with the whole composition in each, so "Niger is half bare soil" is
 * readable without a second click.
 *
 * Series are classes in **legend order** (§G8's fixed categorical order — the order the map's
 * own legend lists them in), so the same class is the same colour in the same position in
 * every row. A class a country does not have gets `0`, not `null`: in a composition an
 * absence is a real zero, and it must not be mistaken for "not reported".
 *
 * **`isos` is part of the answer, not a convenience.** A country whose composition the server
 * could not compute has no row, so the rows are no longer positionally the same list as the
 * `countries` that came in — and every per-row array a caller builds (the cascade's context
 * fade is one) has to be indexed by *these* rows or it describes the wrong country. Returning
 * the kept ISO3s is how a caller can line one up without re-deriving the filter and getting a
 * subtly different answer.
 */
export function stackedRows(
  countries: readonly InsightsCountry[],
  legend: readonly InsightsClass[] | null | undefined,
): { labels: string[]; series: BarsHSeries[]; isos: string[] } {
  const withClasses = countries.filter((c) => (c.classes ?? []).length > 0);
  if (!withClasses.length) return { labels: [], series: [], isos: [] };

  const index = legendIndex(legend);
  // No legend (a body that carried countries but no distribution) — rank the classes the
  // countries themselves name, by their total share, so the stack still has a stable order.
  const order: InsightsClass[] = legend?.length
    ? [...legend]
    : (() => {
        const totals = new Map<number, { c: InsightsClass; total: number }>();
        for (const country of withClasses) {
          for (const c of country.classes ?? []) {
            const seen = totals.get(c.value);
            if (seen) seen.total += c.pct ?? 0;
            else totals.set(c.value, { c, total: c.pct ?? 0 });
          }
        }
        return [...totals.values()].sort((a, b) => b.total - a.total).map((x) => x.c);
      })();

  // `withClasses`, not `countries`: a country the server could not compose has no row to
  // draw, and labelling one anyway leaves a named country with an empty bar beside it —
  // which reads as "this country is 0 % of everything" rather than as "no answer here".
  return {
    labels: withClasses.map((c) => c.label),
    isos: withClasses.map((c) => c.country),
    series: order.map((c) => {
      const entry = index.get(c.value) ?? c;
      return {
        key: `class_${c.value}`,
        label: entry.label,
        color: entry.color ?? UNKNOWN_CLASS_COLOR,
        values: withClasses.map((country) => pctOf(country.classes, c.value) ?? 0),
      };
    }),
  };
}

/**
 * **The partnership pie** (client item 5).
 *
 * `pct` is the wedge, not `value`: the server weights each partnership by how much of the
 * measured quantity sits inside it (`mean × km²`, or the modal class's km²), and `value` is
 * the partnership's own plain number, which the panel prints beside the wedge instead.
 *
 * A zero-share wedge is dropped rather than drawn as a hairline of colour that reads as a
 * partnership with data, and the tail the server folded into `other_pct` gets the one
 * neutral wedge — omitted when there is no tail.
 */
export function partnershipSlices(
  partnerships: InsightsPartnerships | null | undefined,
  otherLabel: string,
): DonutSlice[] {
  if (!partnerships) return [];

  const slices: DonutSlice[] = partnerships.slices
    .map((slice, i) => ({
      id: slice.unit_id,
      label: slice.name,
      value: slice.pct,
      color: PARTNERSHIP_COLORS[i % PARTNERSHIP_COLORS.length] as string,
    }))
    .filter((slice) => Number.isFinite(slice.value) && slice.value > 0);

  if (!slices.length) return [];
  if (Number.isFinite(partnerships.other_pct) && partnerships.other_pct > 0) {
    slices.push({
      id: OTHER_SLICE_ID,
      label: otherLabel,
      value: partnerships.other_pct,
      color: OTHER_COLOR,
    });
  }
  return slices;
}
